import { FC, useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components/macro';
import { useTranslation } from 'react-i18next';
import { DEVICE, TYPOGRAPHY } from '@net0-calc/common/assets/styles';
import { FormApi } from 'final-form';
import { CalculatorFormValues } from 'common/types';

const translationPrefix = 'events.pages.hospitality.form.field';
const presetPrefix = 'events.pages.hospitality.form.presets';

type HotelField = {
  name: keyof CalculatorFormValues;
  label: string;
  stars: number;
};

type PresetValues = {
  percentageOfPeopleInTwoStarHotel: number;
  percentageOfPeopleInThreeStarHotel: number;
  percentageOfPeopleInFourStarHotel: number;
  percentageOfPeopleInFiveStarHotel: number;
};

type Preset = {
  label: string;
  values: PresetValues;
};

type Props = {
  values?: CalculatorFormValues;
  form?: FormApi<CalculatorFormValues>;
};

export const HotelDistribution: FC<Props> = ({ values, form }) => {
  const { t } = useTranslation();

  const fields: HotelField[] = useMemo(
    () => [
      { name: 'percentageOfPeopleInTwoStarHotel', label: t(translationPrefix + '.twoStartHotel.label'), stars: 2 },
      { name: 'percentageOfPeopleInThreeStarHotel', label: t(translationPrefix + '.threeStartHotel.label'), stars: 3 },
      { name: 'percentageOfPeopleInFourStarHotel', label: t(translationPrefix + '.fourStartHotel.label'), stars: 4 },
      { name: 'percentageOfPeopleInFiveStarHotel', label: t(translationPrefix + '.fiveStartHotel.label'), stars: 5 },
    ],
    [t],
  );

  const presets: Preset[] = useMemo(
    () => [
      {
        label: t(presetPrefix + '.budget'),
        values: {
          percentageOfPeopleInTwoStarHotel: 40,
          percentageOfPeopleInThreeStarHotel: 40,
          percentageOfPeopleInFourStarHotel: 15,
          percentageOfPeopleInFiveStarHotel: 5,
        },
      },
      {
        label: t(presetPrefix + '.midRange'),
        values: {
          percentageOfPeopleInTwoStarHotel: 10,
          percentageOfPeopleInThreeStarHotel: 30,
          percentageOfPeopleInFourStarHotel: 40,
          percentageOfPeopleInFiveStarHotel: 20,
        },
      },
      {
        label: t(presetPrefix + '.luxury'),
        values: {
          percentageOfPeopleInTwoStarHotel: 0,
          percentageOfPeopleInThreeStarHotel: 5,
          percentageOfPeopleInFourStarHotel: 35,
          percentageOfPeopleInFiveStarHotel: 60,
        },
      },
      {
        label: t(presetPrefix + '.even'),
        values: {
          percentageOfPeopleInTwoStarHotel: 25,
          percentageOfPeopleInThreeStarHotel: 25,
          percentageOfPeopleInFourStarHotel: 25,
          percentageOfPeopleInFiveStarHotel: 25,
        },
      },
    ],
    [t],
  );

  const getFieldValue = useCallback(
    (name: keyof CalculatorFormValues) => {
      return (values?.[name] as number) || 0;
    },
    [values],
  );

  const currentSum = useMemo(() => {
    return fields.reduce((sum, field) => sum + getFieldValue(field.name), 0);
  }, [fields, getFieldValue]);

  const remaining = 100 - currentSum;

  const applyPreset = useCallback(
    (preset: Preset) => {
      if (!form) return;
      Object.entries(preset.values).forEach(([key, val]) => {
        form.change(key as keyof CalculatorFormValues, val);
      });
    },
    [form],
  );

  const isPresetActive = useCallback(
    (preset: Preset) => {
      return Object.entries(preset.values).every(
        ([key, val]) => getFieldValue(key as keyof CalculatorFormValues) === val,
      );
    },
    [getFieldValue],
  );

  const handleSliderChange = useCallback(
    (fieldName: keyof CalculatorFormValues, newValue: number) => {
      if (!form) return;
      const otherSum = fields.reduce((sum, f) => (f.name === fieldName ? sum : sum + getFieldValue(f.name)), 0);
      const maxAllowed = 100 - otherSum;
      form.change(fieldName, Math.min(newValue, maxAllowed));
    },
    [form, fields, getFieldValue],
  );

  return (
    <Root>
      <PresetSection>
        <PresetLabel>{t(presetPrefix + '.title')}</PresetLabel>
        <PresetRow>
          {presets.map(preset => (
            <PresetButton
              key={preset.label}
              type="button"
              $active={isPresetActive(preset)}
              onClick={() => applyPreset(preset)}>
              {preset.label}
            </PresetButton>
          ))}
        </PresetRow>
      </PresetSection>

      <SlidersSection>
        {fields.map(field => {
          const value = getFieldValue(field.name);
          const otherSum = fields.reduce((sum, f) => (f.name === field.name ? sum : sum + getFieldValue(f.name)), 0);
          const maxAllowed = 100 - otherSum;

          return (
            <SliderRow key={field.name}>
              <SliderHeader>
                <SliderLabel>
                  <Stars>{'★'.repeat(field.stars)}</Stars>
                  {field.label}
                </SliderLabel>
                <SliderValue $hasValue={value > 0}>{value}%</SliderValue>
              </SliderHeader>
              <SliderTrack>
                <SliderFill style={{ width: `${value}%` }} />
                <SliderInput
                  type="range"
                  min={0}
                  max={maxAllowed}
                  value={value}
                  onChange={e => handleSliderChange(field.name, Number(e.target.value))}
                />
              </SliderTrack>
            </SliderRow>
          );
        })}
      </SlidersSection>

      <TotalBar>
        <TotalBarFills>
          {fields.map(field => {
            const value = getFieldValue(field.name);
            return value > 0 ? (
              <TotalSegment key={field.name} style={{ width: `${value}%` }} $stars={field.stars} />
            ) : null;
          })}
        </TotalBarFills>
      </TotalBar>

      <SummaryRow>
        <SummaryText $isError={currentSum > 100}>
          {t('events.pages.hospitality.form.allocated')}: {currentSum}%
        </SummaryText>
        <SummaryText $isError={remaining < 0} $isRemaining>
          {remaining > 0
            ? `${t('events.pages.hospitality.form.remaining')}: ${remaining}%`
            : remaining === 0
            ? t('events.pages.hospitality.form.fullyAllocated')
            : ''}
        </SummaryText>
      </SummaryRow>
    </Root>
  );
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const PresetSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PresetLabel = styled.span`
  ${TYPOGRAPHY.bodyMedium16}
  color: var(--color6);
`;

const PresetRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const PresetButton = styled.button<{ $active: boolean }>`
  ${TYPOGRAPHY.captionRegular14}
  padding: 8px 16px;
  border-radius: 20px;
  border: 1.5px solid var(--color10);
  background: var(--color5);
  color: var(--color6);
  cursor: pointer;
  transition: all 0.2s ease-out;
  white-space: nowrap;

  &:hover {
    border-color: var(--color2);
    color: var(--color2);
  }

  ${({ $active }) =>
    $active &&
    css`
      background: var(--color2);
      border-color: var(--color2);
      color: var(--color5);

      &:hover {
        background: var(--color1);
        border-color: var(--color1);
        color: var(--color5);
      }
    `}
`;

const SlidersSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media ${DEVICE.laptop} {
    gap: 24px;
  }
`;

const SliderRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SliderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SliderLabel = styled.span`
  ${TYPOGRAPHY.bodyRegular16}
  color: var(--color6);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Stars = styled.span`
  color: var(--color2);
  font-size: 14px;
  letter-spacing: 1px;
  min-width: 60px;

  @media ${DEVICE.laptop} {
    font-size: 16px;
    min-width: 70px;
  }
`;

const SliderValue = styled.span<{ $hasValue: boolean }>`
  ${TYPOGRAPHY.bodySemibold18}
  color: ${({ $hasValue }) => ($hasValue ? 'var(--color2)' : 'var(--color7)')};
  min-width: 48px;
  text-align: right;
`;

const SliderTrack = styled.div`
  position: relative;
  height: 8px;
  border-radius: 4px;
  background: var(--color10);
  overflow: visible;
`;

const SliderFill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  border-radius: 4px;
  background: var(--color2);
  transition: width 0.1s ease-out;
  pointer-events: none;
`;

const SliderInput = styled.input`
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  width: 100%;
  height: 24px;
  margin: 0;
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
  cursor: pointer;
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--color5);
    border: 2.5px solid var(--color2);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
    cursor: pointer;
    transition: transform 0.15s ease-out, box-shadow 0.15s ease-out;
  }

  &::-webkit-slider-thumb:hover {
    transform: scale(1.15);
    box-shadow: 0 2px 8px rgba(206, 17, 38, 0.25);
  }

  &::-webkit-slider-thumb:active {
    transform: scale(1.2);
    background: var(--color2);
    box-shadow: 0 2px 12px rgba(206, 17, 38, 0.35);
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--color5);
    border: 2.5px solid var(--color2);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
    cursor: pointer;
  }

  &::-moz-range-track {
    background: transparent;
    border: none;
    height: 8px;
  }
`;

const TotalBar = styled.div`
  width: 100%;
  height: 12px;
  border-radius: 6px;
  background: var(--color10);
  overflow: hidden;
`;

const TotalBarFills = styled.div`
  display: flex;
  height: 100%;
`;

const segmentColors: Record<number, string> = {
  2: 'var(--color4)',
  3: 'var(--color3)',
  4: 'var(--color2)',
  5: 'var(--color1)',
};

const TotalSegment = styled.div<{ $stars: number }>`
  height: 100%;
  background: ${({ $stars }) => segmentColors[$stars] || 'var(--color2)'};
  transition: width 0.15s ease-out;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
`;

const SummaryText = styled.span<{ $isError?: boolean; $isRemaining?: boolean }>`
  ${TYPOGRAPHY.bodyMedium16}
  color: ${({ $isError, $isRemaining }) =>
    $isError ? 'var(--color12)' : $isRemaining ? 'var(--color9)' : 'var(--color6)'};
`;
